import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { findLumAlarmVariable } from 'src/app/components/ui/modal-lum-alarm/modal-lum-alarm.component';
import { DeviceService } from 'src/app/services/devices/device.service';
import { DeviceStateService } from 'src/app/services/device-state/device-state.service';
import { LumSunDayRow, SchedulerLumComponent } from 'src/app/components/scheduler-lum/scheduler-lum.component';
import {
  AstCalendarService,
  calendarFromDate,
  decimalToDms,
} from 'src/app/services/sunTimes/suntimes.service';
import tzLookup from 'tz-lookup';


interface AstroLumElectricalParam {
  labelKey: string;
  icon: string;
  bg: string;
  value: number | string | null;
  unit?: string;
}

// Forma real de data.status (y de status.powermeter en la traza rt): un elemento por fase en arrays.
interface PowerMeterRawStatus {
  v?: number[];
  i?: number[];
  p?: number[];
  q?: number[];
  s?: number[];
  pf?: number[];
  f?: number;
  e?: number;
}

type MaintenancePreview = 'ok' | 'near' | 'pending';

// Valores de muestra para ver la tarjeta de mantenimiento sin límite en el dispositivo.
const MAINTENANCE_PREVIEW: Record<MaintenancePreview, { hours: number; limit: number; pending: boolean; total: number }> = {
  ok: { hours: 1250.5, limit: 4000, pending: false, total: 5713 / 60 },
  near: { hours: 3950, limit: 4000, pending: false, total: 5200.25 },
  pending: { hours: 4100, limit: 4000, pending: true, total: 5350.75 },
};

@Component({
  selector: 'app-astro-lum',
  imports: [CommonModule, FormsModule, TranslateModule, SchedulerLumComponent, NgbPopover],
  templateUrl: './astro-lum.component.html',
  styleUrl: './astro-lum.component.scss',
})
export class AstroLumComponent implements OnInit, OnDestroy {

  @Input() serial = '';

  loading = true;
  deviceInfo: any;
  coordenadas: any;
  ubicacion: any = { country: '', city: '' };
  timeZone = '';
  lastConnection = '';

  scheduleData: any;
  temperatureData: any;
  astcalData: any;
  relayData: any;
  alarmsData: any;
  rtcData: any;
  /** Orto/ocaso de ayer, hoy y mañana (AstCalendarService). */
  sunTimesData: LumSunDayRow[] = [];
  AlarmsTriggered: any[] = [];

  private readonly moduleLoadState = {
    deviceInfo: false,
    temperature: false,
    astcal: false,
    relays: false,
    alarms: false,
    rtc: false,
    scheduler: false,
  };

  electricalParams: AstroLumElectricalParam[] = [
    { labelKey: 'voltage', icon: 'fa-bolt text-warning', bg: 'bg-warning', value: null, unit: 'V' },
    { labelKey: 'current', icon: 'fa-wave-square text-info', bg: 'bg-info', value: null, unit: 'A' },
    { labelKey: 'active_power', icon: 'fa-plug text-success', bg: 'bg-success', value: null, unit: 'W' },
    { labelKey: 'reactive_power', icon: 'fa-rotate text-danger', bg: 'bg-danger', value: null, unit: 'VAr' },
    { labelKey: 'apparent_power', icon: 'fa-chart-line text-dark', bg: 'bg-dark', value: null, unit: 'VA' },
    { labelKey: 'powerFactor', icon: 'fa-gauge-high text-secondary', bg: 'bg-secondary', value: null },
    { labelKey: 'frequency', icon: 'fa-signal text-primary', bg: 'bg-primary', value: null, unit: 'Hz' },
    { labelKey: 'energy', icon: 'fa-battery-full text-success', bg: 'bg-success', value: null, unit: 'Wh' },
    { labelKey: 'temperature', icon: 'fa-temperature-high text-danger', bg: 'bg-danger', value: null, unit: '°C' },
  ];

  // Contador de mantenimiento = partial_minutes (se reinicia); el global (total_minutes) no se toca.
  maintenanceHours: number | null = null;
  totalHours: number | null = null;
  maintenancePending = false;
  // Sin campo en el firmware todavía: de momento solo conmuta la vista (completa o solo horas).
  maintenanceEnabled = true;
  limitHours: number | null = null;
  limitDraft: number | null = null;
  confirmingReset = false;
  // Temporal, solo estética: quitar al conectar límite y reset.
  readonly showPreview = true;
  preview: MaintenancePreview | null = null;
  // true hasta la primera lectura: evita mostrar "off" con confianza antes de saber el estado real.
  outputLoading = true;

  private subs = new Subscription();
  private toggleTimeout?: ReturnType<typeof setTimeout>;
  private rtRenewal?: ReturnType<typeof setInterval>;
  private rtStarted = false;
  // La traza rt caduca sola a los RT_TIME_MIN: si falla el stop de ngOnDestroy, muere en ≤2 min.
  private readonly RT_PERIOD_MS = 2000;
  private readonly RT_TIME_MIN = 2;

  constructor(
    private _device: DeviceService,
    private _state: DeviceStateService,
    private _astCalendar: AstCalendarService,
    private translate: TranslateService,
  ) {}

  // Sin recarga por NavigationEnd: al cambiar de serial device-dashboard destruye y recrea este componente.
  ngOnInit(): void {
    this.loadData();
    this.subscribeLive();
    this.seedPowerMeter();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    clearTimeout(this.toggleTimeout);
    clearInterval(this.rtRenewal);
    if (this.rtStarted) this.setRt(0, 0);
  }

  private loadData(): void {
    this.loading = true;
    this.deviceInfo = null;
    this.coordenadas = null;
    this.ubicacion = { country: '', city: '' };
    this.timeZone = '';
    this.temperatureData = null;
    this.astcalData = null;
    this.relayData = null;
    this.alarmsData = null;
    this.rtcData = null;
    this.sunTimesData = [];
    this.AlarmsTriggered = [];
    this.resetElectricalParams();

    Object.keys(this.moduleLoadState).forEach((key) => {
      this.moduleLoadState[key as keyof typeof this.moduleLoadState] = false;
    });

    this._device.getDeviceInfo(this.serial).subscribe({
      next: (deviceInfo) => {
        this.deviceInfo = deviceInfo;
        this.lastConnection = new Date().toISOString();
        this.markModuleLoaded('deviceInfo');
        // La traza rt solo tiene sentido con el equipo en línea: si está caído, se muestra el stat guardado.
        if (deviceInfo?.alive) this.startRt();
      },
      error: () => {
        this.deviceInfo = null;
        this.markModuleLoaded('deviceInfo');
      },
    });

    this._device.getModuleInfo(this.serial, 'astcal').subscribe({
      next: (data) => {
        this.astcalData = data;
        this.markModuleLoaded('astcal');
        this.timeZone = tzLookup(
          data?.cfg?.latitude ,
          data?.cfg?.longitude ,
        );
        this.rebuildSunTimesData();
        fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${data?.cfg?.latitude}&lon=${data?.cfg?.longitude}&format=json`,
        )
          .then((res) => res.json())
          .then((data) => {
            this.ubicacion = {
              country: data.address.country,
              city:
                data.address.city || data.address.town || data.address.village,
            };
          })
          .catch((error) => {
            console.error('Error fetching location:', error);
          });
      },
      error: () => {
        this.astcalData = null;
        this.markModuleLoaded('astcal');
      },
    });

    this._device.getModuleInfo(this.serial, 'scheduler').subscribe({
      next: (data) => {
        this.scheduleData = data;
        this.markModuleLoaded('scheduler');
      },
      error: () => {
        this.scheduleData = null;
        this.markModuleLoaded('scheduler');
      },
    });

    this._device.getModuleInfo(this.serial, 'alarms').subscribe({
      next: (data) => {
        this.alarmsData = data;
        this.markModuleLoaded('alarms');
        this.applyAlarmsValue();;
      },
      error: () => {
        this.alarmsData = null;
        this.markModuleLoaded('alarms');
      },
    });

    this._device.getModuleInfo(this.serial, 'rtc' ,"stat" ,{ localdata: true }).subscribe({
      next: (data) => {
        this.rtcData = data;
        this.rebuildSunTimesData();
        this.markModuleLoaded('rtc');
      },
      error: () => {
        this.rtcData = null;
        this.markModuleLoaded('rtc');
      },
    });
  }

  // relays, temperatura y medidas: GET inicial vía DeviceStateService y desde ahí websocket (sin polling).
  private subscribeLive(): void {
    // La copia de BD puede venir atrasada: la corrige el websocket. relayData conserva la forma { cfg, stat }.
    this.subs.add(this._state.select(this.serial, 'relays', 'all').subscribe(({ cfg, status }) => {
      this.relayData = { cfg, stat: status };
      // Firmware ≥0.4.0: las horas van en el estado del relé (GET inicial y notificaciones del websocket).
      if (status?.total_minutes != null) this.totalHours = status.total_minutes / 60;
      if (status?.partial_minutes != null) this.maintenanceHours = status.partial_minutes / 60;
      if (status?.maintenance_pending !== undefined) this.maintenancePending = !!status.maintenance_pending;
      this.outputLoading = false;
      clearTimeout(this.toggleTimeout);
      this.touch();
      this.markModuleLoaded('relays');
    }));

    this.subs.add(this._state.select(this.serial, 'temperature', 'all').subscribe(({ status }) => {
      this.temperatureData = status;
      this.applyTemperatureValue({ stat: status });
      this.markModuleLoaded('temperature');
    }));

    // stat de BD al entrar. Una notificación sin medidas (equipo caído) no debe borrar esa copia.
    this.subs.add(this._state.select(this.serial, 'powermeter', 'all').subscribe(({ status }) => {
      if (!status) return;
      this.applyPowerMeter(status, !this.hasInstantMeasure(status));
    }));
    this.subs.add(this._state.select(this.serial, 'rt').subscribe(({ status }) => {
      const pm = status?.powermeter;
      if (!pm || !this.hasInstantMeasure(pm)) return;
      this.applyPowerMeter(pm);
      this.touch();
    }));
  }

  // La pantalla de módulos enseña el stat guardado. Esta lectura es la misma copia, sin pasar por la traza rt.
  private seedPowerMeter(): void {
    this._device.getModuleInfo(this.serial, 'powermeter', 'all').subscribe({
      next: (resp) => {
        const item = resp?.items?.[0] ?? resp;
        const stat = item?.stat ?? item?.status ?? resp?.stat ?? resp?.status;
        if (stat) this.applyPowerMeter(stat, !this.hasInstantMeasure(stat));
      },
    });
  }

  private applyPowerMeter(status: PowerMeterRawStatus | Record<string, unknown>, keepStored = false): void {
    const raw = status as Record<string, unknown>;
    const round = (n: number | null, d: number) => n == null ? null : Math.round(n * 10 ** d) / 10 ** d;
    const values: Record<string, number | null> = {
      voltage: round(this.firstPhase(raw['v'] ?? raw['voltage_rms']), 1),
      current: round(this.firstPhase(raw['i'] ?? raw['current_rms']), 2),
      active_power: round(this.firstPhase(raw['p'] ?? raw['active_power']), 1),
      reactive_power: round(this.firstPhase(raw['q'] ?? raw['reactive_power']), 1),
      apparent_power: round(this.firstPhase(raw['s'] ?? raw['apparent_power']), 1),
      powerFactor: round(this.firstPhase(raw['pf'] ?? raw['power_factor']), 2),
      frequency: round(this.asNumber(raw['f'] ?? raw['frequency']), 1),
      energy: round(this.asNumber(raw['e'] ?? raw['energy_active']), 1),
    };
    this.electricalParams.forEach((param) => {
      if (!(param.labelKey in values)) return;
      const next = values[param.labelKey];
      if (next == null && keepStored) return;
      param.value = next;
    });
  }

  // Una muestra en vivo tiene tensión, corriente, potencia o frecuencia. Energía a 0 no basta para pisar el stat.
  private hasInstantMeasure(status: PowerMeterRawStatus | Record<string, unknown>): boolean {
    const raw = status as Record<string, unknown>;
    return [raw['v'], raw['i'], raw['p'], raw['f'] ?? raw['frequency'], raw['voltage_rms'], raw['current_rms']]
      .some((value) => this.firstPhase(value) != null);
  }

  private firstPhase(raw: unknown): number | null {
    if (Array.isArray(raw)) return this.asNumber(raw[0]);
    if (typeof raw === 'string' && raw.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return this.asNumber(parsed[0]);
      } catch {
        return null;
      }
    }
    return this.asNumber(raw);
  }

  private asNumber(raw: unknown): number | null {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    if (typeof raw === 'string' && raw.trim() !== '') {
      const value = Number(raw);
      return Number.isFinite(value) ? value : null;
    }
    return null;
  }

  private touch(): void {
    this.lastConnection = new Date().toISOString();
  }

  // 0/0 → stop. Body plano (confirmado): el backend lo envuelve en data.cfg.
  private setRt(periodMs: number, timeMin: number): void {
    this._device.setModuleCmd(this.serial, { period_ms: periodMs, time_min: timeMin }, 'rt', 'set').subscribe({
      error: err => console.warn('rt/set', err)
    });
  }

  // No es polling: renueva la traza rt antes de que caduque; las muestras llegan por websocket.
  // periodic_ms del powermeter no se toca: el dispositivo ya envía cada 15 min por defecto.
  private startRt(): void {
    if (this.rtStarted) return;
    this.rtStarted = true;
    this.setRt(this.RT_PERIOD_MS, this.RT_TIME_MIN);
    this.rtRenewal = setInterval(() => this.setRt(this.RT_PERIOD_MS, this.RT_TIME_MIN), 60_000);
  }

  private markModuleLoaded(module: keyof typeof this.moduleLoadState): void {
    this.moduleLoadState[module] = true;
    this.tryFinishLoading();
  }

  private tryFinishLoading(): void {
    const allLoaded = Object.values(this.moduleLoadState).every(Boolean);
    if (allLoaded) {
      this.loading = false;
    }
  }

  private resetElectricalParams(): void {
    this.electricalParams.forEach((param) => {
      param.value = null;
    });
  }

  private applyTemperatureValue(data: any): void {
    const temperature = this.extractTemperatureValue(data);
    const param = this.electricalParams.find((item) => item.labelKey === 'temperature');
    if (param) {
      param.value = temperature;
    }
  }

  private extractTemperatureValue(data: any): number | null {
    const stat = data?.stat ?? data?.status ?? data;
    const raw = stat?.temperature ?? stat?.temperature_C ?? stat?.value ?? stat?.temp;
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      return null;
    }
    return Math.round(value * 10) / 10;
  }

  /** Ayer, hoy y mañana con Meeus; hoy se refina en el gráfico con astcal.stat. */
  private rebuildSunTimesData(): void {
    const lat = Number(this.astcalData?.cfg?.latitude) ;
    const lng = Number(this.astcalData?.cfg?.longitude) ;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      this.sunTimesData = [];
      return;
    }

    const cfg = this.astcalData?.cfg ?? {};
    const corrSunriseMin = cfg.dawn_corr ?? cfg.sunrise_corr ?? cfg.corrSunrise ?? 0;
    const corrSunsetMin = cfg.dusk_corr ?? cfg.sunset_corr ?? cfg.corrSunset ?? 0;
    const corrSunrise = Math.round(Number(corrSunriseMin) || 0);
    const corrSunset = Math.round(Number(corrSunsetMin) || 0);
    const deviceToday = this.getDeviceReferenceDate();
    deviceToday.setHours(0, 0, 0, 0);

    const rows: LumSunDayRow[] = [];
    for (let slot = -1; slot <= 1; slot++) {
      const day = new Date(deviceToday);
      day.setDate(deviceToday.getDate() + slot);

      const tzOffsetMinutes = this.getTzOffsetMinutes(day, this.timeZone);
      const result = this._astCalendar.getSunTimes(
        calendarFromDate(day),
        tzOffsetMinutes,
        decimalToDms(lat),
        decimalToDms(lng),
        corrSunrise,
        corrSunset,
      );

      rows.push({
        slot,
        date: new Date(day),
        sunriseMin: result.sunrise,
        sunsetMin: result.sunset,
        sunriseStr: result.sunriseStr,
        sunsetStr: result.sunsetStr,
      });
    }

    this.sunTimesData = rows;
  }

  private getDeviceReferenceDate(): Date {
    const status = this.rtcData?.status ?? this.rtcData?.stat ?? this.rtcData;
    const ts = Number(status?.local_timestamp);
    if (Number.isFinite(ts) && ts > 0) {
      return new Date(ts * 1000);
    }

    const parsed = this.parseRtcLocalDatetime(status?.local_datetime);
    return parsed ?? new Date();
  }

  private parseRtcLocalDatetime(value: unknown): Date | null {
    if (typeof value !== 'string' || !value) {
      return null;
    }

    const match = value.match(/^(\d{2}):(\d{2}):(\d{2})(\d{4}-\d{2}-\d{2})$/);
    if (!match) {
      return null;
    }

    const [, hh, mm, ss, datePart] = match;
    const [year, month, day] = datePart.split('-').map(Number);
    const hour = Number(hh);
    const minute = Number(mm);
    const second = Number(ss);

    if ([year, month, day, hour, minute, second].some((n) => !Number.isFinite(n))) {
      return null;
    }

    return new Date(year, month - 1, day, hour, minute, second, 0);
  }

  private getTzOffsetMinutes(date: Date, timeZone: string): number {
    if (!timeZone) {
      return -date.getTimezoneOffset();
    }

    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
      }).formatToParts(date);
      const tzName = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
      const match = tzName.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
      if (!match) {
        return -date.getTimezoneOffset();
      }

      const sign = match[1] === '-' ? -1 : 1;
      const hours = Number(match[2]);
      const minutes = Number(match[3] ?? 0);
      return sign * (hours * 60 + minutes);
    } catch {
      return -date.getTimezoneOffset();
    }
  }

  /** true = ON, false = OFF, null = sin dato */
  get isOutputOn(): boolean | null {
    const state = this.relayData?.stat?.state;
    if (state == null || state === '') return null;
    return String(state).toLowerCase() === 'on';
  }

  /** true = candado cerrado (permanent), false = abierto, null = sin dato */
  get isPermanentLocked(): boolean | null {
    // El frame del websocket lo trae en stat; la lectura inicial puede traerlo solo en cfg.
    const permanent = this.relayData?.stat?.permanent ?? this.relayData?.cfg?.permanent;
    if (permanent == null || permanent === '') return null;
    return Number(permanent) === 1;
  }

  toggleOutput(): void {
    // El firmware (obj_relay.c) quita el bloqueo permanent con cualquier toggle que no lo pida:
    // se desbloquea solo desde Ajustes > Canales.
    if (this.outputLoading || this.isPermanentLocked) return;
    this.outputLoading = true;
    this._device.setModuleCmd(this.serial, {}, 'relays', 'toggle').subscribe({
      // Confirma el websocket; si la notificación se pierde, se relee el estado real del dispositivo.
      next: () => {
        if (!this.outputLoading) return;
        this.toggleTimeout = setTimeout(() => {
          this.outputLoading = false;
          this._state.refresh(this.serial, 'relays', 'all');
        }, 5000);
      },
      error: () => { this.outputLoading = false; }
    });
  }

  get view() {
    const demo = this.preview && MAINTENANCE_PREVIEW[this.preview];
    const hours = demo ? demo.hours : this.maintenanceHours;
    const limit = demo ? demo.limit : this.limitHours;
    const pending = demo ? demo.pending : this.maintenancePending;
    const percent = hours != null && limit ? Math.min(100, hours / limit * 100) : 0;
    return {
      hours, limit, pending, percent,
      total: demo ? demo.total : this.totalHours,
      remaining: hours != null && limit ? limit - hours : null,
      tone: pending ? 'danger' : percent >= 90 ? 'warning' : 'success',
    };
  }

  get canSaveLimit(): boolean {
    return !!this.limitDraft && this.limitDraft > 0 && this.limitDraft !== this.limitHours;
  }

  // Sin backend todavía: guardar el límite y el reset solo cierran la interacción.
  saveLimit(): void { }

  confirmReset(): void {
    this.confirmingReset = false;
  }

  applyAlarmsValue(): void {
    this.AlarmsTriggered = [];
    const alarms = this.alarmsData?.alarms ?? [];
    alarms.forEach((alarm: any) => {
      if (alarm.stat && alarm.stat.triggered == 1) {
        this.AlarmsTriggered.push(alarm);
      }
    });
  }

  alarmVariableKey(alarm: any): string {
    return findLumAlarmVariable(alarm?.cfg)?.labelKey ?? '';
  }

  alarmConditionText(alarm: any): string {
    const cfg = alarm?.cfg ?? {};
    const unit = findLumAlarmVariable(cfg)?.unit ?? '';
    const suffix = unit ? ` ${unit}` : '';

    if (cfg.threshold_type === 'MIN_MAX') {
      const label = this.translate.instant('lumAlarmThresholdMinMax');
      return `${label}: ${cfg.threshold_min ?? '--'} – ${cfg.threshold_max ?? '--'}${suffix}`;
    }

    const key = cfg.threshold_type === 'MIN' ? 'lumAlarmConditionLess' : 'lumAlarmConditionGreater';
    return `${this.translate.instant(key)} ${cfg.threshold ?? '--'}${suffix}`;
  }

  alarmActionKey(alarm: any): string {
    return alarm?.cfg?.trigger_actions?.[0]
      ? 'lumAlarmComponentRelayNotification'
      : 'lumAlarmComponentNotification';
  }

  alarmRelayActionKey(alarm: any): string {
    const command = alarm?.cfg?.trigger_actions?.[0]?.action;
    if (command === 'on') {
      return 'lumAlarmActionOn';
    }
    if (command === 'off') {
      return 'lumAlarmActionOff';
    }
    return '';
  }


}
